import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  TextField,
  Box,
  IconButton,
  Button,
  Grid,
  CircularProgress,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/CloseRounded";
import PropTypes from "prop-types";

const CompanyExpiredDateDialogComponent = ({
  selectedCompany,
  onCompanyDialogCloseClick,
  onEditDateClick,
  loading,
}) => {
  const [editMode, setEditMode] = useState(false);
  const [monthsInput, setMonthsInput] = useState("");

  const [plan, setPlan] = useState(selectedCompany?.company?.plan || "");
  // Update state when selectedCompany changes (after API load)
  useEffect(() => {
    if (selectedCompany) {
      setPlan(selectedCompany.company?.plan || "");
      setMonthsInput("");
    }
  }, [selectedCompany]);

  // Store the original expired date for reset
  const originalExpiredAt = selectedCompany?.company?.expired_at || "";

  // Helper: parse yyyy-mm-dd to Date
  const parseDate = (str) => {
    if (!str || !/^\d{4}-\d{2}-\d{2}$/.test(str)) return null;
    const [y, m, d] = str.split("-").map(Number);
    return new Date(y, m - 1, d);
  };

  // Calculate preview expired date using SaaS business logic
  const getPreviewBusinessLogic = (months) => {
    if (!months || Number.isNaN(months) || months <= 0) return "-";
    const now = new Date();
    const expiredDate = parseDate(originalExpiredAt);
    let baseDate;
    if (expiredDate && expiredDate > now) {
      baseDate = new Date(expiredDate);
    } else {
      baseDate = now;
    }
    const currentDay = baseDate.getDate();
    baseDate.setMonth(baseDate.getMonth() + months);
    const year = baseDate.getFullYear();
    const month = baseDate.getMonth() + 1;
    const lastDay = new Date(year, month, 0).getDate();
    const day = Math.min(currentDay, lastDay);
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(
      2,
      "0"
    )}`;
  };

  if (!selectedCompany) {
    return null;
  }

  const handleSave = () => {
    const monthsToAdd = Number(monthsInput);
    if (!monthsInput || Number.isNaN(monthsToAdd) || monthsToAdd <= 0) {
      // Optionally show an error to the user here
      return;
    }
    if (onEditDateClick) {
      const newExpiredAt = getPreviewBusinessLogic(monthsToAdd);
      onEditDateClick({
        id: selectedCompany.company.id,
        expiredAt: newExpiredAt,
        plan,
      });
    }
    setEditMode(false);
  };

  return (
    <Dialog
      open={!!selectedCompany}
      onClose={onCompanyDialogCloseClick}
      scroll="paper"
      aria-labelledby="scroll-dialog-title"
      aria-describedby="scroll-dialog-description"
      fullWidth
    >
      <DialogTitle id="scroll-dialog-title">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body2">
            <b>{selectedCompany.company ? selectedCompany.company.name : ""}</b>
          </Typography>
          <IconButton
            size="small"
            color="inherit"
            onClick={onCompanyDialogCloseClick}
            style={{ padding: 0 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={3}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Current Expired Date
                </Typography>
                <Box
                  sx={{
                    border: "1px solid #c4c4c4",
                    borderRadius: "4px",
                    padding: "16.5px 14px",
                    background: "#f9f9f9",
                    color: "#333",
                    minHeight: "56px",
                    display: "flex",
                    alignItems: "center",
                    fontSize: "1rem",
                  }}
                >
                  <Typography variant="body1">
                    {originalExpiredAt || "-"}
                  </Typography>
                </Box>
              </Grid>
              {/* Preview new expired date using SaaS business logic */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Preview New Expired Date
                </Typography>
                <Box
                  sx={{
                    border: "1px dashed #90caf9",
                    borderRadius: "4px",
                    padding: "12px 14px",
                    background: "#e3f2fd",
                    color: "#1565c0",
                    minHeight: "40px",
                    display: "flex",
                    alignItems: "center",
                    fontSize: "0.95rem",
                  }}
                >
                  <Typography variant="body2">
                    <b>Preview </b>{" "}
                    {getPreviewBusinessLogic(Number(monthsInput))}
                    <br />
                    <span style={{ fontSize: "0.95em" }}>
                      {(() => {
                        const now = new Date();
                        const expiredDate = parseDate(originalExpiredAt);
                        if (expiredDate && expiredDate > now) {
                          return "Extending from current expired date.";
                        }
                        return "Extending from today (already expired or today).";
                      })()}
                    </span>
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Change Expired Validity (Months)"
                  name="expired_months"
                  value={editMode ? monthsInput : ""}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^\d]/g, "");
                    setMonthsInput(val);
                  }}
                  variant="outlined"
                  disabled={!editMode}
                  placeholder="e.g. 12, 24, 36"
                  inputProps={{ pattern: "\\d+", maxLength: 4 }}
                  helperText="Enter number of months, e.g. 12, 24, 36 (from current expired date)"
                />
              </Grid>
            </Grid>
            <Box mt={3} display="flex" justifyContent="flex-end">
              {editMode ? (
                <>
                  <Button
                    onClick={() => setEditMode(false)}
                    style={{ marginRight: 8 }}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={handleSave}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} /> : "Save"}
                  </Button>
                </>
              ) : (
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => setEditMode(true)}
                  disabled={loading}
                >
                  Edit
                </Button>
              )}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

CompanyExpiredDateDialogComponent.propTypes = {
  selectedCompany: PropTypes.object,
  onCompanyDialogCloseClick: PropTypes.func.isRequired,
  onEditDateClick: PropTypes.func,
  loading: PropTypes.bool,
};

CompanyExpiredDateDialogComponent.defaultProps = {
  loading: false,
};

export default CompanyExpiredDateDialogComponent;
